'use client'

import { useEffect, useRef, useState, type RefObject } from 'react'

export type ScrollFadeState = {
  canScrollLeft: boolean
  canScrollRight: boolean
  canScrollTop: boolean
  canScrollBottom: boolean
}

export function useScrollFade(existingRef?: RefObject<HTMLDivElement | null>) {
  const internalRef = useRef<HTMLDivElement>(null)
  const scrollRef = existingRef ?? internalRef

  const [state, setState] = useState<ScrollFadeState>({
    canScrollLeft: false,
    canScrollRight: false,
    canScrollTop: false,
    canScrollBottom: false,
  })

  const prevPos = useRef<{ scrollLeft: number; scrollTop: number } | null>(null)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const update = () => {
      const { scrollLeft, scrollTop, clientWidth, clientHeight, scrollWidth, scrollHeight } = el

      // Skip if scroll position hasn't changed
      if (prevPos.current?.scrollLeft === scrollLeft && prevPos.current?.scrollTop === scrollTop) return
      prevPos.current = { scrollLeft, scrollTop }

      const next: ScrollFadeState = {
        canScrollLeft: scrollLeft > 0,
        canScrollRight: scrollLeft + clientWidth < scrollWidth - 1,
        canScrollTop: scrollTop > 0,
        canScrollBottom: scrollTop + clientHeight < scrollHeight - 1,
      }

      setState((prev) => {
        if (
          prev.canScrollLeft === next.canScrollLeft &&
          prev.canScrollRight === next.canScrollRight &&
          prev.canScrollTop === next.canScrollTop &&
          prev.canScrollBottom === next.canScrollBottom
        )
          return prev

        return next
      })
    }

    update()
    el.addEventListener('scroll', update, { passive: true })

    const observer = new ResizeObserver(() => {
      prevPos.current = null
      update()
    })
    observer.observe(el)

    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [scrollRef])

  return { ...state, scrollRef }
}
